import React, { useEffect } from 'react';
import * as bxlPOS from '../BixolonJS/bxlpos.js';
import { amtStr } from 'helpers/numFormatter';
import BigNumber from 'bignumber.js';
import {
	OrderDiscountStatus,
	PaymentClass,
	useGetTaxSchemeDetailLazyQuery,
} from 'generated/graphql';
import { toProperCase } from 'helpers/hooks/stringNumberFunction/stringConverter';
import { alignment } from './BillReceiptFunctions';
import { SchemeType } from 'containers/OutletModule/CommonSettings/TaxSetup/TaxSetupListing';

export const BillReceiptFooter = (
	total,
	discount,
	discountCode,
	subtotal,
	serviceCharge,
	tax,
	mode,
	query,
	latestPayment,
	chargeInfo1,
	chargeInfo2,
	currencyCode,
	rounding,
	taxSchemeInfo,
	getTaxSchemeDetail,
) => {
	// function
	const handleCardType = cardType => {
		switch (cardType) {
			case 'VISA_CARD':
				return 'VISA';
			case 'MASTERCARD':
				return 'MASTERCARD';
			case 'AMERICAN_EXPRESS_CARD':
				return 'AMERICAN EXPRESS';
			case 'DINERS_CARD':
				return 'DINERS';
			case 'JCB_CARD':
				return 'JCB';
			case 'UNIONPAY_CARD':
				return 'UNIONPAY';
			case 'DEBIT_CARD':
				return 'DEBIT';
			default:
				return 'Credit';
		}
	};

	const taxAmount = taxType => {
		let totalTax;
		if (taxType === SchemeType?.ServiceCharge) {
			totalTax = taxSchemeInfo?.totalSC;
		} else if (taxType === SchemeType?.ServiceTax) {
			totalTax = taxSchemeInfo?.totalSST;
		} else if (taxType === SchemeType?.GST) {
			totalTax = taxSchemeInfo?.totalGST;
		}
		return totalTax;
	};

	// sub total
	alignment(` `, `\n`, false);
	alignment(`Sub-Total`, `${currencyCode + amtStr(subtotal)}\n`, false);

	// discount
	const activeOrderDiscount =
		mode === 'bill-settlement'
			? query?.orderDiscount?.filter(
					y => y?.status === OrderDiscountStatus?.Active,
			  )[0]
			: query?.order?.orderDiscount?.filter(
					y => y?.status === OrderDiscountStatus?.Active,
			  )[0];

	alignment(
		`Discount ${discountCode?.filter(
			x => x?.ID === activeOrderDiscount?.discountID,
		)[0]?.code ?? ' '}`,
		`(${currencyCode}${amtStr(discount)})\n`,
		false,
	);

	// rounding
	if (mode === 'bill-settlement') {
		if (rounding < 0) {
			alignment(
				`Rounding`,
				`(${currencyCode}${Math.abs(rounding).toFixed(2)})\n`,
				false,
			);
		} else {
			alignment(`Rounding`, `${currencyCode}${amtStr(rounding)}\n`, false);
		}
	} else {
		if (latestPayment?.roundingAmount < 0) {
			alignment(
				`Rounding`,
				`(${currencyCode}${Math.abs(latestPayment?.roundingAmount).toFixed(
					2,
				)})\n`,
				false,
			);
		} else {
			alignment(
				`Rounding`,
				`${currencyCode}${amtStr(latestPayment?.roundingAmount)}\n`,
				false,
			);
		}
	}

	// total
	alignment(` `, `\n`, true);
	alignment(
		`Total`,
		`${currencyCode}${amtStr(
			new BigNumber(Math.abs(total))
				.plus(latestPayment?.roundingAmount ?? 0)
				.toNumber(),
		)}\n`,
		true,
	);
	alignment(` `, `\n`, true);

	// tax
	if (getTaxSchemeDetail?.length > 0) {
		getTaxSchemeDetail?.map(x => {
			alignment(
				`${x?.taxCode} (${x?.taxRate}%)`,
				`${currencyCode}${amtStr(Number(taxAmount(x?.taxType)))}\n`,
				false,
			);
		});
	}

	// bill settlement
	if (mode === 'bill-settlement') {
		alignment(` `, `\n`, true);
		alignment(`Name : ___________________________________`, `\n\n`, false);
		alignment(`Room/ACC : _______________________________`, `\n\n`, false);
		alignment(`Signature : ______________________________`, `\n`, false);
	}

	if (mode === 'void-bill' || mode === 'bill-settlement') {
	} else {
		alignment(` `, `\n`, true);
		alignment(`Payment`, `\n`, true);

		if (latestPayment !== undefined) {
			if (latestPayment?.paymentClass === PaymentClass.UnsettledBill) {
				alignment(
					`Unsettled Bill`,
					`${currencyCode}${amtStr(latestPayment?.amount)}\n`,
					false,
				);
			} else if (latestPayment?.paymentClass === PaymentClass.Credit) {
				alignment(
					`${handleCardType(latestPayment?.creditCardType)}`,
					`${currencyCode}${amtStr(latestPayment?.receivedAmount)}\n`,
					false,
				);
			} else if (latestPayment?.paymentClass === PaymentClass.Online) {
				alignment(
					`Online Payment`,
					`${currencyCode}${amtStr(latestPayment?.receivedAmount)}\n`,
					false,
				);
			} else if (latestPayment?.paymentClass === PaymentClass.NonGuest) {
				alignment(
					`Non Guest`,
					`${currencyCode}${amtStr(latestPayment?.receivedAmount)}\n`,
					false,
				);
			} else if (latestPayment?.paymentClass === PaymentClass.SuspendFolio) {
				alignment(
					`Suspend Folio`,
					`${currencyCode}${amtStr(latestPayment?.receivedAmount)}\n`,
					false,
				);
			} else if (latestPayment?.paymentClass === PaymentClass.BankTt) {
				alignment(
					`Bank TT`,
					`${currencyCode}${amtStr(latestPayment?.receivedAmount)}\n`,
					false,
				);
			} else {
				alignment(
					`${toProperCase(latestPayment?.paymentClass)}`,
					`${currencyCode}${amtStr(latestPayment?.receivedAmount)}\n`,
					false,
				);
			}
		} else {
			const OCENT = discountCode?.filter(
				x => x?.ID === activeOrderDiscount?.discountID,
			)[0]?.discountType;

			const displayOCENT =
				OCENT === 'OFFICER_CHECK'
					? 'Officer Check'
					: OCENT === 'ENTERTAINMENT'
					? 'Entertainment'
					: OCENT === 'DISCOUNT'
					? 'Discount'
					: 'Promo';

			alignment(
				`${displayOCENT}`,
				`${currencyCode}${amtStr(latestPayment?.amount)}\n`,
				false,
			);
		}

		if (
			latestPayment?.paymentClass === PaymentClass.Debtor ||
			latestPayment?.paymentClass === PaymentClass.Room ||
			latestPayment?.paymentClass === PaymentClass.NonGuest ||
			latestPayment?.paymentClass === PaymentClass.SuspendFolio ||
			latestPayment?.paymentClass === PaymentClass.Officer ||
			latestPayment?.paymentClass === PaymentClass.Staff
		) {
			alignment(`Charged to`, `${chargeInfo1} | ${chargeInfo2}\n`, false);
		}

		if (latestPayment?.paymentClass === PaymentClass.BankTt) {
			alignment(`Remark: `, `${chargeInfo2}\n`, false);
		}

		if (latestPayment?.paymentClass === PaymentClass.Voucher) {
			alignment(`Code: `, `${latestPayment?.referenceNo}\n`, false);
		}

		if (latestPayment?.paymentClass === PaymentClass.Credit) {
			alignment(
				`  Card ref: `,
				`${`****${latestPayment?.cardNo?.slice(-4)}` ?? '-'}  \n`,
				false,
			);
		}

		if (latestPayment?.paymentClass === PaymentClass.Others) {
			alignment(`Remarks: `, `${latestPayment?.remark}\n`, false);
		}

		if (
			latestPayment?.paymentClass === PaymentClass.Cash &&
			latestPayment?.changeAmount > 0
		) {
			alignment(
				`Change: `,
				`${currencyCode}${amtStr(latestPayment?.changeAmount)}\n`,
				false,
			);
		}

		alignment(` `, `\n`, false);
		bxlPOS.printText(
			`Thank you. Please come again!\n`,
			0,
			0,
			false,
			false,
			false,
			0,
			1,
		);
	}
};
